"use client";
import {
  TextField,
  Box,
  TextFieldProps,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { pxToVwAreaClientes } from "@/utils/pxToVw";
import { MOBILE_BASE } from "@/theme/theme";
import { useRef, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";

type FormTextFieldSimpleProps = TextFieldProps & {
  marginBottom?: number;
  marginBottomMobile?: number;
  spanColor?: string;
  startAdornmentText?: string;
  numberOfLines?: number;
  dontShowPasswordToggle?: boolean;
  isStartAdornmentTooLong?: boolean;
  forcedTooLong?: boolean;
  textColor?: string;
};

export default function FormTextFieldSimple({
  marginBottom = 0,
  marginBottomMobile = 0,
  spanColor = "#858276",
  textColor = "#472600",
  startAdornmentText,
  dontShowPasswordToggle = false,
  isStartAdornmentTooLong = undefined,
  forcedTooLong = false,
  ...props
}: FormTextFieldSimpleProps) {
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const focusInput = () => {
    inputRef.current?.focus();
  };

  isStartAdornmentTooLong =
    isStartAdornmentTooLong === undefined
      ? startAdornmentText
        ? startAdornmentText.length > 18
        : false
      : isStartAdornmentTooLong;

  return (
    <Box
      mb={{
        xs: props.helperText ? pxToVwAreaClientes(-8, MOBILE_BASE) : 0,
        md: props.helperText ? pxToVwAreaClientes(-16) : 0,
      }}
      display={"flex"}
    >
      <TextField
        {...props}
        inputRef={inputRef}
        variant="standard"
        fullWidth
        sx={{
          ".MuiInputBase-root": {
            display: {
              xs: isStartAdornmentTooLong || forcedTooLong ? "block" : "flex",
              md: forcedTooLong ? "block" : "flex",
            },
            input: {
              letterSpacing: props.value != "---" ? "normal" : "-2px",
              color: textColor,
              display: {
                xs:
                  isStartAdornmentTooLong || forcedTooLong
                    ? "inline-block"
                    : "flex",
                md: forcedTooLong ? "inline-block" : "flex",
              },
              width: {
                xs:
                  isStartAdornmentTooLong || forcedTooLong
                    ? "calc(100% - 40px)"
                    : "100%",
                md: forcedTooLong ? "calc(100% - 40px)" : "100%",
              },
            },
            ".MuiInputAdornment-positionEnd": {
              display: {
                xs:
                  isStartAdornmentTooLong || forcedTooLong
                    ? "inline-block"
                    : "flex",
                md: forcedTooLong ? "inline-block" : "flex",
              },
            },
          },
          mt: 0,
          mb: {
            xs: pxToVwAreaClientes(marginBottomMobile, MOBILE_BASE),
            md: pxToVwAreaClientes(marginBottom),
          },
        }}
        FormHelperTextProps={{
          sx: {
            fontSize: {
              xs: pxToVwAreaClientes(16, MOBILE_BASE), // Change this value
              md: pxToVwAreaClientes(16), // Change this value
            },
          },
        }}
        type={showPassword ? "text" : props.type}
        InputProps={{
          startAdornment: (
            <Box
              onClick={focusInput}
              onMouseDown={(event) => event.preventDefault()}
              sx={{ cursor: "text" }}
              alignSelf={props.multiline ? "flex-start" : "center"}
              flexDirection={"column"}
            >
              <Box
                className="brmobile"
                component="span"
                sx={{
                  alignSelf: props.multiline ? "flex-start" : "center",
                  marginTop: props.multiline
                    ? {
                        xs: pxToVwAreaClientes(0, MOBILE_BASE),
                        md: pxToVwAreaClientes(0),
                      }
                    : 0,
                  color: spanColor,
                  fontSize: {
                    xs: pxToVwAreaClientes(16, MOBILE_BASE),
                    md: pxToVwAreaClientes(16),
                  },

                  marginRight: props.multiline
                    ? 0
                    : {
                        xs: pxToVwAreaClientes(8, MOBILE_BASE),
                        md: pxToVwAreaClientes(8),
                      },
                  paddingRight: !props.multiline
                    ? 0
                    : {
                        xs: pxToVwAreaClientes(8, MOBILE_BASE),
                        md: pxToVwAreaClientes(8),
                      },
                  lineHeight: props.multiline
                    ? {
                        xs: pxToVwAreaClientes(62, MOBILE_BASE),
                        md: pxToVwAreaClientes(62.85),
                      }
                    : { xs: "120%", md: "110%" },

                  fontFamily: "var(--font-romek), sans-serif",
                  whiteSpace: "nowrap",
                  textTransform: "uppercase",
                  borderBottom: props.multiline ? "1px solid #858276" : "none",
                }}
              >
                {startAdornmentText ? (
                  <span
                    dangerouslySetInnerHTML={{ __html: startAdornmentText }}
                  />
                ) : null}
              </Box>
              {props.multiline && (
                <>
                  <Box
                    component="span"
                    sx={{
                      pt: {
                        xs: pxToVwAreaClientes(62, MOBILE_BASE),
                        md: pxToVwAreaClientes(62),
                      },
                      borderBottom: "1px solid #858276",
                    }}
                  ></Box>
                  <Box
                    component="span"
                    sx={{
                      pt: {
                        xs: pxToVwAreaClientes(62, MOBILE_BASE),
                        md: pxToVwAreaClientes(62),
                      },
                      borderBottom: "1px solid #858276",
                    }}
                  ></Box>
                  {props.numberOfLines && props.numberOfLines > 3 && (
                    <Box
                      component="span"
                      sx={{
                        pt: {
                          xs: pxToVwAreaClientes(62.5, MOBILE_BASE),
                          md: pxToVwAreaClientes(62.5),
                        },
                        borderBottom: "1px solid #858276",
                      }}
                    ></Box>
                  )}
                </>
              )}
            </Box>
          ),
          endAdornment:
            props.type === "password" && !dontShowPasswordToggle ? (
              <InputAdornment
                position="end"
                sx={{
                  xs: {
                    position:
                      isStartAdornmentTooLong || forcedTooLong
                        ? "absolute"
                        : "static",
                  },
                  md: {},
                }}
              >
                <IconButton
                  onClick={() => setShowPassword((show) => !show)}
                  onMouseDown={(event) => event.preventDefault()}
                  edge="end"
                >
                  {showPassword ? (
                    <VisibilityOff sx={{ color: "#D4D1C8" }} />
                  ) : (
                    <Visibility sx={{ color: "#D4D1C8" }} />
                  )}
                </IconButton>
              </InputAdornment>
            ) : null,
          disableUnderline: props.multiline,
          sx: {
            // esto afecta al <input>
            fontSize: {
              xs: pxToVwAreaClientes(16, MOBILE_BASE),
              md: pxToVwAreaClientes(16),
            },
            lineHeight: { xs: "120%", md: "110%" },
            fontFamily: "var(--font-romek), sans-serif",
            color: "#472600", // color del texto del usuario
            "& textarea": {
              // cada línea de texto simula un renglón
              backgroundImage:
                "linear-gradient(to top, #858276 1px, transparent 1px)",
              backgroundSize: {
                xs: "100% " + pxToVwAreaClientes(63, MOBILE_BASE),
                md: "100% " + pxToVwAreaClientes(63),
              }, // 👈 altura de línea
              lineHeight: {
                xs: pxToVwAreaClientes(63, MOBILE_BASE),
                md: pxToVwAreaClientes(63),
              },
              fontSize: {
                xs: pxToVwAreaClientes(16, MOBILE_BASE),
                md: pxToVwAreaClientes(16),
              },
              fontFamily: "var(--font-romek), sans-serif",
              color: "#472600",
              resize: "none", // opcional: evitar que el usuario cambie la altura
            },
          },
        }}
        InputLabelProps={{ shrink: false }}
      />
    </Box>
  );
}
